import type { RepeatConfig, RequestType } from "@entities";
import { DEFAULT_OPPORTUNITY_VOLUNTEER_MODE } from "@shared/helpers/opportunity-volunteer-mode";
import {
  DEFAULT_VOLUNTEER_REGISTRATION_FLOW,
} from "@shared/helpers/volunteer-registration-outcome";
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  type FC,
} from "react";
import { useOrganization } from "./OrganizationProvider";

export const RequestContext = createContext<{
  repeatConfig: RepeatConfig;
  setRepeatConfig: React.Dispatch<React.SetStateAction<RepeatConfig>>;
  currentEvent: RequestType;
  setCurrentEvent: React.Dispatch<React.SetStateAction<RequestType>>;
  /** Timestamp (ms) of the latest AI apply. Used to prevent post-apply re-hydration races. */
  lastAiApplyAt: number;
  markAiAppliedNow: () => void;
  resetRepeatConfig: VoidFunction;
  resetRequest: VoidFunction;
  isTouched: boolean;
  setIsTouched: React.Dispatch<React.SetStateAction<boolean>>;
} | null>(null);

export const useRequest = () => useContext(RequestContext)!;
interface Props {
  children?: React.ReactNode;
}

export const REQUEST_DEFAULT_VALUES = {
  amount: 1,
  defaultMatchWeight: 1,
  startDate: new Date(),
  endDate: new Date(),
  lastCallToVolunteers: (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 1);
    return d;
  })(),
  isRepeated: false,
  location: "",
  comments: "",
  coordinates: { lat: 0, lng: 0 },
  destination: null,
  destinationCoordinates: { lat: 0, lng: 0 },
  contactName: undefined,
  contactPhoneNumber: undefined,
  contactPosition: undefined,
  minAge: 16,
  maxAge: 120,
  progressStatus: undefined,
  managerUrgency: "STANDARD" as RequestType["managerUrgency"],
  callCenterUrgency: 0,
  callCenterDifficulty: 3,
  isRemote: false,
};

export const REQUEST_DEFAULT_FIELDS = {
  ...REQUEST_DEFAULT_VALUES,
  title: "",
  description: "",
  hasTime: true,
  hasStartTime: true,
  isRepeated: false,
  selectedCategories: [],
  selectedPopulations: [],
  volunteerRegistrationFlow: DEFAULT_VOLUNTEER_REGISTRATION_FLOW,
  isHidden: false,
  isOpportunityGroupParent: false,
  isLongTerm: false,
  volunteerMode: DEFAULT_OPPORTUNITY_VOLUNTEER_MODE,
  notificationTemplates: {
    participantReminder: "",
    newParticipant: "",
  },
};

export const REQUEST_DEFAULT_REPEAT_CONFIG: RepeatConfig = {
  repeatType: "NO_REPEAT",
  repeatInterval: 1,
  days: [],
};

export const RequestProvider: FC<Props> = ({ children }) => {
  const { currOrganization } = useOrganization();
  const [repeatConfig, setRepeatConfig] = useState<RepeatConfig>(
    REQUEST_DEFAULT_REPEAT_CONFIG,
  );
  const [currentEvent, setCurrentEvent] = useState<RequestType>(
    REQUEST_DEFAULT_FIELDS as any,
  );

  const [isTouched, setIsTouched] = useState<boolean>(false);
  const [lastAiApplyAt, setLastAiApplyAt] = useState<number>(0);

  useEffect(() => {
    setCurrentEvent((prev) => ({
      ...prev,
      organization: currOrganization,
      isReportRequired: !!currOrganization?.hasDefaultReport,
      imageId:
        prev.imageId || currOrganization?.defaultOpportunityBackgroundImageId,
      isPublic: false,
    }));
  }, [currOrganization?.id]);

  function resetRepeatConfig() {
    setRepeatConfig(REQUEST_DEFAULT_REPEAT_CONFIG);
    setCurrentEvent((prev) => ({ ...prev, isRepeated: false }));
  }

  function resetRequest() {
    setCurrentEvent({
      ...REQUEST_DEFAULT_FIELDS,
      isPublic: false,
    });
    setRepeatConfig(REQUEST_DEFAULT_REPEAT_CONFIG);
    setIsTouched(false);
    setLastAiApplyAt(0);
  }

  return (
    <RequestContext.Provider
      value={{
        resetRequest,
        resetRepeatConfig,
        repeatConfig,
        setRepeatConfig,
        currentEvent,
        setCurrentEvent,
        lastAiApplyAt,
        markAiAppliedNow: () => setLastAiApplyAt(Date.now()),
        isTouched,
        setIsTouched,
      }}
    >
      {children}
    </RequestContext.Provider>
  );
};
